import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useStyletron } from 'baseui';
import { Button } from 'baseui/button';
import { Heading, HeadingLevel } from 'baseui/heading';
import { Input } from 'baseui/input';
import { Skeleton } from 'baseui/skeleton';
import { toaster } from 'baseui/toast';
import axios from 'axios';
import { FilePill, OrganizationPill, OrganizationSelect, OrganizationSelectMulti, TwoColumnGrid } from '@gtintel/platform-atoms';
import { useAbortableAsyncOp, useDebounce } from '@gtintel/platform-utils';
import { ScopeComponentsContext } from '../ProjectApp/context';
import Editor from '../components/Editor';
import FileUpload from '../components/FileUpload';
import { AddWithName, List, ListContext } from '../components/List';
import { ScopeComponentList } from '../components/ScopeComponent';
import { useFlux } from '../state/useFlux';
import { initialState, mutations } from './state';
const TemplatesContext = React.createContext(null);
const TemplateLibraryContext = React.createContext(null);
const TemplatePanel = ({
  template,
  organizations
}) => {
  const [css, theme] = useStyletron();
  const ctx = useContext(TemplatesContext);
  const [nameEditor, setNameEditor] = useState(false);
  const nameComponent = nameEditor ? <Input onChange={e => ctx.edit({
    name: e.target.value
  })} value={template.name} onBlur={() => setNameEditor(false)} clearOnEscape autoFocus /> : <button className={css({
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent'
  })} onClick={() => setNameEditor(true)}>
      <Heading>{template.name}</Heading>
    </button>;
  const onChange = useCallback(instructionsHtml => {
    ctx.edit({
      instructionsHtml
    });
  }, [ctx]);
  return <div className={css({
    backgroundColor: theme.colors.backgroundPrimary,
    padding: '48px'
  })} data-testid="template-panel">
      <HeadingLevel>
        <Button kind="secondary" className={css({
        float: 'right',
        marginRight: '14px'
      })} size="mini" onClick={() => ctx.close()}>
          Close
        </Button>
        <Heading>{nameComponent}</Heading>

        <div className={css({
        marginBottom: '32px'
      })}>
          <Heading styleLevel={5}>Additional Info</Heading>
          <Editor onChange={onChange} value={template.instructionsHtml} />
        </div>

        <TwoColumnGrid>
          <div>
            <Heading styleLevel={5}>Report Template</Heading>
            {!template.reportTemplate ? <FileUpload handleUpload={async file => ctx.addAttachment(template.id, file, 'reportTemplate')} overrides={{
            Root: {
              style: {
                maxWidth: '1000px'
              }
            }
          }} /> : <FilePill testId="template-report-template" key={template.reportTemplate.id} file={template.reportTemplate} closeButtonFunc={() => ctx.removeAttachment(template.id, template.reportTemplate.id, 'reportTemplate')} />}
          </div>
          <div>
            <Heading styleLevel={5}>Example</Heading>
            {!template.sampleReport ? <FileUpload handleUpload={async file => ctx.addAttachment(template.id, file, 'sampleReport')} overrides={{
            Root: {
              style: {
                maxWidth: '1000px'
              }
            }
          }} /> : <FilePill testId="template-sample-report" key={template.sampleReport.id} file={template.sampleReport} closeButtonFunc={() => ctx.removeAttachment(template.id, template.sampleReport.id, 'sampleReport')} />}
          </div>
        </TwoColumnGrid>

        <TwoColumnGrid>
          <div>
            <Heading styleLevel={5}>Client</Heading>
            <OrganizationSelect value={template.organization} changeOrganization={organizationId => ctx.edit({
            organization: organizationId
          })} organizations={organizations && Object.values(organizations)} />
          </div>

          <div>
            <Heading styleLevel={5}>Turn Around Time</Heading>
            <Input data-testid="turn-around-time-input" type="number" value={template.turn_around_time} onChange={e => ctx.edit({
            turn_around_time: Number(e.target.value)
          })} endEnhancer="days" />
          </div>
        </TwoColumnGrid>

        <ScopeComponentList />

        <div className={css({
        backgroundColor: theme.colors.backgroundPrimary,
        padding: '1em'
      })}>
          <h3>Create Scope Components from an Excel file</h3>
          <FileUpload handleUpload={async file => {
          ctx.importScopes(template.id, file);
        }} />
        </div>
      </HeadingLevel>
    </div>;
};
const TemplateCollection = ({
  organizations
}) => {
  const ctx = useContext(TemplatesContext);
  const [organizationsFilter, setOrganizationsFilter] = useState([]);
  const [css, theme] = useStyletron();
  const listItems = () => {
    const items = [];
    ctx.list().filter(template => {
      if (organizationsFilter.length > 0) {
        return template.organization && organizationsFilter.includes(template.organization);
      }
      return true;
    }).map(template => {
      items.push({
        ...template,
        copyable: true,
        removeable: true,
        children: <div key={template.id} className={css({
          display: 'flex',
          flexFlow: 'row nowrap',
          justifyContent: 'space-between',
          width: '100%'
        })}>
              <h5>{template.name}</h5>
              {template.organization ? <OrganizationPill internal_code={organizations[template.organization].internal_code} name={organizations[template.organization].name} /> : <OrganizationPill internal_code={'No Client Set'} name={'No Client Set'} />}
            </div>
      });
    });
    return items;
  };
  return <div data-testid="template-collection">
      <OrganizationSelectMulti value={organizationsFilter} changeOrganization={setOrganizationsFilter} organizations={organizations && Object.values(organizations)} />

      <ListContext.Provider value={ctx}>
        <List items={listItems()} onItemClick={ctx.open} />
        <AddWithName />
      </ListContext.Provider>
      <div className={css({
      backgroundColor: theme.colors.backgroundPrimary,
      padding: '1em'
    })}>
        <h3>Create Template from an Excel file</h3>
        <FileUpload handleUpload={async file => {
        ctx.import(file);
      }} />
      </div>
    </div>;
};
const TemplateLibrary = () => {
  const {
    api
  } = useContext(TemplateLibraryContext);
  const [css] = useStyletron();
  const [state, mutate] = useFlux(initialState());
  const [openTemplateId, setOpenTemplateId] = useState(null);
  const updateTemplate = useDebounce((openTemplateId, changes) => {
    api.project.updateTemplate(openTemplateId, changes);
  });
  const updateScope = useDebounce(api.project.updateScope);
  const fetchLibraryAsync = useMemo(() => () => {
    const abortController = new AbortController();
    const promise = api.project.getTemplateLibrary({
      signal: abortController.signal
    });
    promise.then(({
      data
    }) => mutate(mutations.init({
      state: data
    })));
    return [promise, abortController];
  }, [api, mutate]);
  const {
    isLoading,
    fn: fetchLibrary
  } = useAbortableAsyncOp(fetchLibraryAsync, {
    isLoading: true
  });
  useEffect(() => {
    const [, abortController] = fetchLibrary();
    return () => abortController.abort();
  }, []); // only run once, when the component mounts

  const templatesCtx = {
    open: setOpenTemplateId,
    edit: changes => {
      mutate(mutations.edit({
        changes,
        id: openTemplateId
      }));
      updateTemplate(openTemplateId, changes);
    },
    close: () => setOpenTemplateId(null),
    addWithName: async name => {
      return api.project.createTemplate({
        name
      }).then(({
        data: template
      }) => {
        mutate(mutations.add({
          template
        }));
      });
    },
    remove: async id => {
      mutate(mutations.delete({
        id
      }));
      api.project.deleteTemplate(id);
    },
    copy: async id => {
      const result = await api.project.copyTemplate(id);
      mutate(mutations.add({
        ...result.data
      }));
    },
    importScopes: async (templateId, fileData) => {
      const formData = new FormData();
      formData.append('file', fileData);
      const result = await api.project.importScopesFromXlsx(templateId, formData);
      if (result.status === 200) {
        mutate(mutations.scope.import({
          templateId,
          scopes: result.data.scopes
        }));
      } else {
        console.error(result.data);
        toaster.negative('Could not import scopes due to a server error. Please ensure the sheet has a Name and Description column.');
      }
    },
    import: async fileData => {
      const formData = new FormData();
      formData.append('file', fileData);
      const result = await api.project.importTemplatesFromXlsx(formData);
      if (result.status === 200) {
        mutate(mutations.import({
          templates: result.data.templates,
          scopes: result.data.scopes
        }));
        if (Object.keys(result.data.errors).length > 0) {
          Object.keys(result.data.errors).forEach(key => {
            toaster.negative(`Could not import sheet ${key} due to ${result.data.errors[key]}`);
          });
        }
      } else {
        console.error(result.data);
        toaster.negative('Could not import templates due to a server error. Please ensure the sheet/s all have a Name and Description column.');
      }
    },
    list: () => Object.values(state.template.index),
    addAttachment: async (templateId, file, type) => {
      const resp = await api.attachment.getPresignedUrl({
        type: 'template',
        id: templateId,
        filename: file.name,
        file_type: file.type
      });
      const formData = new FormData();
      for (const key in resp.data.presigned.fields) {
        formData.append(key, resp.data.presigned.fields[key]);
      }
      formData.append('file', file);
      try {
        await axios.post(resp.data.presigned.url, formData);
      } catch (e) {
        console.log(`Request failed with: ${e}`);
      }
      let result;
      let changeType;
      if (type === 'reportTemplate') {
        result = await api.template.addAttachmentToTemplate(templateId, {
          filename: file.name,
          path: resp.data.path,
          type: 'report_template',
          size: file.size
        });
        changeType = mutations.document.attachReportTemplate;
      } else {
        result = await api.template.addAttachmentToTemplate(templateId, {
          filename: file.name,
          path: resp.data.path,
          type: 'sample_report',
          size: file.size
        });
        changeType = mutations.document.attachSampleReport;
      }
      if (result.status === 201) {
        mutate(changeType({
          type: changeType,
          templateId,
          attachment: result.data.attachment
        }));
      }
    },
    removeAttachment: async (templateId, attachmentId, type) => {
      const result = await api.attachment.delete(attachmentId);
      let changeType;
      if (type === 'reportTemplate') {
        changeType = mutations.document.removeReportTemplate;
      } else {
        changeType = mutations.document.removeSampleReport;
      }
      if (result.status === 204) {
        mutate(changeType({
          type: changeType,
          templateId
        }));
      }
    }
  };
  const scopesCtx = {
    addWithName: async name => {
      const result = await api.project.createTemplateScope(openTemplateId, {
        name
      });
      mutate(mutations.scope.add({
        templateId: openTemplateId,
        scope: result.data
      }));
    },
    list: () => {
      return state.template.index[openTemplateId].scopes.map(id => state.scope.index[id]);
    },
    relatesTo() {
      return state.scope.relates_to.map(i => ({
        id: i[0],
        label: i[1]
      }));
    },
    applicability: () => {
      return state.scope.applicability || [];
    },
    serviceTypes() {
      return state.scope.service_types;
    },
    edit: (scopeId, changes) => {
      mutate(mutations.scope.edit({
        changes,
        id: scopeId
      }));
      updateScope(scopeId, changes);
    },
    remove: async scopeId => {
      mutate(mutations.scope.delete({
        scopeId,
        templateId: openTemplateId
      }));
      api.project.deleteScope(scopeId);
    },
    copy: async scopeId => {
      const result = await api.project.copyScope(scopeId);
      mutate(mutations.scope.add({
        scope: result.data,
        templateId: openTemplateId
      }));
    },
    reorder: async (oldIndex, newIndex) => {
      const scopes = [...state.template.index[openTemplateId].scopes];
      const e = scopes.splice(oldIndex, 1)[0];
      scopes.splice(newIndex, 0, e);
      mutate(mutations.scope.reorder({
        templateId: openTemplateId,
        scopeIds: scopes
      }));
      return api.project.updateTemplateComponentsOrder(openTemplateId, scopes);
    }
  };
  if (isLoading) {
    return <div className={css({
      backgroundColor: '#fff',
      padding: '40px'
    })}>
        <Skeleton rows={8} height="500px" width="100%" animation autoSizeRows overrides={{
        Row: {
          style: {
            marginBottom: '24px'
          }
        }
      }} />
      </div>;
  }
  if (openTemplateId === null) {
    return <TemplatesContext.Provider value={templatesCtx}>
        <TemplateCollection organizations={state.organizations} />
      </TemplatesContext.Provider>;
  }
  return <TemplatesContext.Provider value={templatesCtx}>
      <ScopeComponentsContext.Provider value={scopesCtx}>
        <TemplatePanel template={state.template.index[openTemplateId]} organizations={state.organizations} />
      </ScopeComponentsContext.Provider>
    </TemplatesContext.Provider>;
};
export { TemplateLibrary, TemplateLibraryContext, TemplateCollection, TemplatePanel, TemplatesContext };